import { InlineIcon } from "@iconify/react";
import { useQuery } from "@tanstack/react-query";
import Fuse from "fuse.js";
import { useMemo, useState } from "react";
import { useApoAuth } from "../../../../../hooks";
import { getAppUserList } from "../api";

interface UserOption {
  id: number;
  label: string;
  first_name: string;
  last_name: string;
  email: string;
  username: string;
}

interface SubagentUserSelectorProps {
  isDarkMode: boolean;
  selectedSubagent: string | null;
}

export function SubagentUserSelector({
  isDarkMode,
  selectedSubagent,
}: SubagentUserSelectorProps) {
  const [userSearchQuery, setUserSearchQuery] = useState("");
  const [selectedUsername, setSelectedUsername] = useState<string>("");
  const { getIdTokenClaims } = useApoAuth();

  // Extract subagent ID from selectedSubagent (format: "123___subagent")
  const selectedSubagentId = selectedSubagent?.split("___")[0];
  const selectedSubagentSourceEntity = selectedSubagent ? `${selectedSubagentId}___subagent` : null;

  // Fetch users for selected subagent
  const { data: usersData, isFetching: isUsersFetching } = useQuery({
    queryKey: ["subagentUsers", selectedSubagentSourceEntity],
    queryFn: async () => {
      if (!selectedSubagentSourceEntity) {
        throw new Error("No subagent selected");
      }
      const token = await getIdTokenClaims();
      if (!token || !("__raw" in token)) {
        throw new Error("No authentication token available");
      }
      return getAppUserList(selectedSubagentSourceEntity, (token as { __raw: string }).__raw);
    },
    enabled: !!selectedSubagentSourceEntity && !!getIdTokenClaims,
    refetchOnWindowFocus: false,
  });

  // Transform users data into options
  const userOptions = useMemo(() => {
    const options: UserOption[] = [];

    if (usersData?.results) {
      for (const user of usersData.results) {
        if (!user) continue;

        const label = `${user.first_name} ${user.last_name} - ${user.email}`;

        options.push({
          id: user.id,
          label,
          first_name: user.first_name,
          last_name: user.last_name,
          email: user.email,
          username: user.username,
        });
      }
      // Sort by label
      options.sort((a, b) => a.label.localeCompare(b.label));
    }

    return options;
  }, [usersData]);

  // Filter users using FuseJS fuzzy search
  const filteredUsers = useMemo(() => {
    if (!userSearchQuery.trim()) {
      return userOptions;
    }

    const fuseOptions = {
      shouldSort: true,
      keys: ["first_name", "last_name", "email", "username", "label"],
      threshold: 0.4,
      minMatchCharLength: 1,
    };

    const fuse = new Fuse(userOptions, fuseOptions);
    const results = fuse.search(userSearchQuery);
    return results.map((result) => result.item);
  }, [userOptions, userSearchQuery]);

  if (!selectedSubagent) {
    return null;
  }

  return (
    <>
      <input type="hidden" name="selectedUsername" value={selectedUsername} />
      <div
        className={[
          "rounded-lg",
          "shadow-md",
          "border",
          "overflow-hidden",
          isDarkMode ? "bg-gray-700 border-gray-600" : "bg-gray-50 border-gray-200",
        ].join(" ")}
      >
      <div
        className={[
          "px-4",
          "py-3",
          "border-b",
          isDarkMode ? "bg-gray-800 border-gray-600" : "bg-white border-gray-200",
        ].join(" ")}
      >
        <h3
          className={`text-lg! font-semibold ${isDarkMode ? "text-gray-100" : "text-gray-900"}`}
        >
          Sub-agent Users
        </h3>
      </div>
      <div className="p-4 space-y-3">
        <div className="relative">
          <input
            type="text"
            placeholder="Search users..."
            value={userSearchQuery}
            onChange={(e) => setUserSearchQuery(e.target.value)}
            className={[
              "w-full",
              "px-3",
              "py-2",
              "pr-10",
              "rounded-md",
              "border",
              "focus:outline-none",
              "focus:ring-2",
              "focus:ring-blue-500",
              isDarkMode
                ? "bg-gray-800 border-gray-600 text-gray-100 placeholder-gray-400"
                : "bg-white border-gray-300 text-gray-900 placeholder-gray-500",
            ].join(" ")}
          />
          <InlineIcon
            icon="mdi:magnify"
            className={[
              "absolute",
              "right-3",
              "top-1/2",
              "-translate-y-1/2",
              "w-5",
              "h-5",
              isDarkMode ? "text-gray-400" : "text-gray-500",
            ].join(" ")}
          />
        </div>
        {isUsersFetching ? (
          <p
            className={`text-sm text-center py-4 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
          >
            Loading users...
          </p>
        ) : filteredUsers.length === 0 ? (
          <p
            className={`text-sm text-center py-4 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
          >
            {userSearchQuery ? "No users found" : "No users available"}
          </p>
        ) : (
          <div
            className={[
              "max-h-64",
              "overflow-y-auto",
              "border",
              "rounded-md",
              isDarkMode ? "border-gray-600" : "border-gray-200",
            ].join(" ")}
          >
            {filteredUsers.map((user) => (
              <label
                key={user.id}
                className={[
                  "w-full",
                  "px-3",
                  "py-2",
                  "border-b",
                  "last:border-b-0",
                  "flex",
                  "items-center",
                  "gap-3",
                  "cursor-pointer",
                  "hover:bg-opacity-50",
                  isDarkMode
                    ? "text-gray-200 border-gray-600 hover:bg-gray-700"
                    : "text-gray-900 border-gray-200 hover:bg-gray-100",
                ].join(" ")}
              >
                <input
                  type="radio"
                  name="selectedUser"
                  value={user.id}
                  className="mt-1"
                  onChange={() => setSelectedUsername(user.username)}
                />
                <div className="flex-1">
                  <div className="text-sm font-medium">
                    {user.first_name} {user.last_name}
                  </div>
                  <div
                    className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                  >
                    {user.email}
                  </div>
                  <div
                    className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                  >
                    @{user.username}
                  </div>
                </div>
              </label>
            ))}
          </div>
        )}
      </div>
    </div>
    </>
  );
}

